import { useCallback, useEffect, useState, type RefObject } from 'react';
import Button from 'devextreme-react/button';
import Tabs from 'devextreme-react/tabs';
import type { FileReviewPanelPlacement } from '@jarvis/platform-file-review';
import { downloadPdfFile, printPdfFile, sharePdfViewerLink } from './pdfViewerActions';
import { PdfDiscussionPositionButton } from './PdfViewerToolbarActions';
import './PdfPreviewRibbon.css';

type PreviewRibbonTab = 'file' | 'view' | 'discussions';

const TAB_ITEMS: { id: PreviewRibbonTab; text: string }[] = [
  { id: 'file', text: 'File' },
  { id: 'view', text: 'View' },
  { id: 'discussions', text: 'Discussions' }
];

export interface PdfPreviewRibbonProps {
  mountId: string;
  relativePath: string;
  disabled?: boolean;
  /** When false, hide Comment on selection (annotate not permitted). */
  canCommentOnSelection?: boolean;
  /** When false, hide the Discussions ribbon tab (e.g. Public folder). */
  discussionsEnabled?: boolean;
  chatOpen: boolean;
  onToggleChat: () => void;
  showOverlays: boolean;
  onToggleOverlays: () => void;
  onCommentOnSelection?: () => void;
  placement: FileReviewPanelPlacement;
  onDiscussionPlacementChange: (placement: FileReviewPanelPlacement) => void;
  /** Element to put into browser fullscreen (preview stage). */
  fullscreenTargetRef?: RefObject<HTMLElement | null>;
}

export function PdfPreviewRibbon({
  mountId,
  relativePath,
  disabled = false,
  canCommentOnSelection = true,
  discussionsEnabled = true,
  chatOpen,
  onToggleChat,
  showOverlays,
  onToggleOverlays,
  onCommentOnSelection,
  placement,
  onDiscussionPlacementChange,
  fullscreenTargetRef
}: PdfPreviewRibbonProps) {
  const [activeTab, setActiveTab] = useState<PreviewRibbonTab>('file');
  const [isFullscreen, setIsFullscreen] = useState(false);

  const tabItems = discussionsEnabled
    ? TAB_ITEMS
    : TAB_ITEMS.filter((tab) => tab.id !== 'discussions');

  useEffect(() => {
    if (!discussionsEnabled && activeTab === 'discussions') {
      setActiveTab('file');
    }
  }, [activeTab, discussionsEnabled]);

  useEffect(() => {
    function onFullscreenChange() {
      setIsFullscreen(Boolean(document.fullscreenElement));
    }
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const onDownload = useCallback(() => {
    downloadPdfFile(mountId, relativePath);
  }, [mountId, relativePath]);

  const onShare = useCallback(() => {
    void sharePdfViewerLink(mountId, relativePath);
  }, [mountId, relativePath]);

  const onPrint = useCallback(() => {
    void printPdfFile(mountId, relativePath);
  }, [mountId, relativePath]);

  const onToggleFullscreen = useCallback(async () => {
    const target = fullscreenTargetRef?.current ?? document.documentElement;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        return;
      }
      await target.requestFullscreen();
    } catch {
      /* browser may block fullscreen */
    }
  }, [fullscreenTargetRef]);

  return (
    <div className="pdf-preview-ribbon" role="region" aria-label="PDF ribbon">
      {/*
        WebKit clears the document selection on mouse-down outside the PDF text
        layer. Suppressing the default on the Discussions tab strip and Comment
        on selection keeps the highlight visible for the STR in ITB-1791.
      */}
      <span
        className="pdf-preview-ribbon__keep-selection"
        onMouseDown={(e) => e.preventDefault()}
      >
        <Tabs
          className="pdf-preview-ribbon__tabs"
          dataSource={tabItems}
          keyExpr="id"
          selectedItemKeys={[activeTab]}
          onSelectionChanged={(e) => {
            const next = e.addedItems?.[0] as { id?: PreviewRibbonTab } | undefined;
            if (next?.id) setActiveTab(next.id);
          }}
          scrollingEnabled={false}
          showNavButtons={false}
          focusStateEnabled={false}
        />
      </span>
      <div className="pdf-preview-ribbon__items" role="toolbar" aria-label={`${activeTab} tools`}>
        {activeTab === 'file' ? (
          <>
            <Button
              icon="download"
              text="Download"
              stylingMode="text"
              disabled={disabled}
              onClick={onDownload}
            />
            <Button
              icon="share"
              text="Share"
              stylingMode="text"
              disabled={disabled}
              onClick={onShare}
            />
            <Button
              icon="print"
              text="Print"
              stylingMode="text"
              disabled={disabled}
              onClick={onPrint}
            />
          </>
        ) : null}
        {activeTab === 'view' ? (
          <Button
            icon="fullscreen"
            text={isFullscreen ? 'Exit Full Screen' : 'Full Screen'}
            stylingMode="text"
            disabled={disabled}
            onClick={() => void onToggleFullscreen()}
          />
        ) : null}
        {discussionsEnabled && activeTab === 'discussions' ? (
          <>
            {onCommentOnSelection ? (
              <span
                className="pdf-preview-ribbon__keep-selection"
                onMouseDown={(e) => e.preventDefault()}
              >
                <Button
                  icon="comment"
                  text="Comment on selection"
                  stylingMode="text"
                  disabled={disabled || !canCommentOnSelection}
                  onClick={onCommentOnSelection}
                />
              </span>
            ) : null}
            <Button
              icon={showOverlays ? 'eyeopen' : 'eyeclose'}
              text={showOverlays ? 'Highlights on' : 'Highlights off'}
              stylingMode="text"
              disabled={disabled}
              onClick={onToggleOverlays}
            />
            <Button
              icon="comment"
              text={chatOpen ? 'Hide discussion' : 'Discussion panel'}
              stylingMode="text"
              disabled={disabled}
              onClick={onToggleChat}
            />
            <span className="pdf-preview-ribbon__sep" aria-hidden="true" />
            <PdfDiscussionPositionButton
              value={placement}
              disabled={disabled}
              onChange={onDiscussionPlacementChange}
              showText
            />
          </>
        ) : null}
      </div>
    </div>
  );
}
